<template>
  <div v-if="editing" class="tw:flex tw:items-center tw:h-9 tw:pl-1 tw:pr-1 tw:gap-1">
    <span class="drag-handle tw:w-5 tw:h-8 tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:cursor-grab tw:text-slate-500 tw:hover:text-slate-100" title="Drag to move">
      <GripVertical class="tw:w-4 tw:h-4" />
    </span>

    <input
      v-if="renaming"
      ref="input"
      v-model="draftTitle"
      class="section-title-input tw:flex-1 tw:min-w-0 tw:h-8 tw:px-2 tw:rounded-lg tw:text-sm tw:font-bold tw:text-slate-100 tw:bg-slate-800 tw:border tw:border-solid tw:border-indigo-500 tw:outline-none"
      :maxlength="MAX_SIDEBAR_TEXT_LENGTH"
      aria-label="Section name"
      @keydown.enter.prevent="commitRename"
      @keydown.esc.prevent="cancelRename"
      @blur="commitRename"
    />
    <button
      v-else
      type="button"
      class="tw:flex-1 tw:min-w-0 tw:text-left tw:text-sm tw:font-bold tw:text-slate-100 tw:truncate tw:cursor-text"
      title="Rename"
      @click="$emit('start-rename')"
    >
      {{ section.section.title }}
    </button>

    <button
      type="button"
      class="tw:w-7 tw:h-7 tw:rounded-lg tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:cursor-pointer tw:text-slate-400 tw:hover:text-slate-100 tw:hover:bg-slate-800"
      :aria-label="`Options for ${section.section.title}`"
      aria-haspopup="true"
      @click="menu?.toggle($event)"
    >
      <EllipsisVertical class="tw:w-4 tw:h-4" />
    </button>
    <Menu ref="menu" :model="menuItems" popup />
  </div>

  <!-- The 72px icon rail has no room for titles; a rule marks where a section starts instead. -->
  <div v-else-if="collapsedDesktop" class="tw:border-t tw:border-slate-800/40 tw:mx-1 tw:mt-3 tw:mb-2"></div>

  <button
    v-else
    type="button"
    class="tw:w-full tw:h-6 tw:flex tw:items-center tw:gap-2 tw:cursor-pointer tw:text-slate-500 tw:hover:text-slate-300 tw:transition-colors"
    :class="variant === 'desktop' ? 'tw:px-3' : 'tw:px-4'"
    :aria-expanded="!collapsed"
    @click="$emit('toggle')"
  >
    <span class="tw:text-xs tw:font-bold tw:uppercase tw:tracking-wider tw:truncate tw:min-w-0">{{ section.section.title }}</span>
    <span
      v-if="collapsed && containsActive"
      class="tw:w-1.5 tw:h-1.5 tw:rounded-full tw:bg-[#FF5352] tw:shrink-0"
      title="The current page is in this section"
    ></span>
    <ChevronDown
      class="tw:w-3.5 tw:h-3.5 tw:ml-auto tw:shrink-0 tw:transition-transform tw:duration-200"
      :class="{ 'tw:-rotate-90': collapsed }"
    />
  </button>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import Menu from 'primevue/menu'
import type { MenuItem } from 'primevue/menuitem'
import { ChevronDown, EllipsisVertical, GripVertical } from '@lucide/vue'
import { MAX_SIDEBAR_TEXT_LENGTH } from './sidebarLayout'
import type { ResolvedSidebarSection } from './useSidebarLayout'

const props = defineProps<{
  section: ResolvedSidebarSection
  variant: 'desktop' | 'mobile'
  isPinned: boolean
  editing: boolean
  renaming: boolean
  containsActive: boolean
}>()

const emit = defineEmits<{
  toggle: []
  'start-rename': []
  rename: [title: string]
  'cancel-rename': []
  delete: []
}>()

const menu = ref<InstanceType<typeof Menu> | null>(null)
const input = ref<HTMLInputElement | null>(null)
const draftTitle = ref('')

const collapsed = computed(() => !!props.section.section.collapsed)
const collapsedDesktop = computed(() => props.variant === 'desktop' && !props.isPinned)

const menuItems: MenuItem[] = [
  { label: 'Rename', icon: 'pi pi-pencil', command: () => emit('start-rename') },
  { label: 'Delete', icon: 'pi pi-trash', command: () => emit('delete') }
]

watch(() => props.renaming, async renaming => {
  if (!renaming) {
    return
  }
  draftTitle.value = props.section.section.title
  await nextTick()
  input.value?.focus()
  input.value?.select()
}, { immediate: true })

// Enter commits and then blurs the input as it unmounts, which would commit a second time.
let settled = false
watch(() => props.renaming, renaming => { if (renaming) settled = false })

function commitRename () {
  if (settled) return
  settled = true
  const title = draftTitle.value.trim()
  // A blank name is not a rename; it puts the old name back.
  if (title && title !== props.section.section.title) {
    emit('rename', title)
  } else {
    emit('cancel-rename')
  }
}

function cancelRename () {
  if (settled) return
  settled = true
  emit('cancel-rename')
}
</script>
